import { useEffect, useMemo, useState } from 'react';

import { RootComponentInstance } from '@uniformdev/canvas';
import {
	RenderComponentResolver,
	UniformComposition,
	UniformPlayground,
	UniformPlaygroundDecorator,
} from '@uniformdev/canvas-react';
import { Quirks } from '@uniformdev/context';
import { useUniformContext } from '@uniformdev/context-react';
import { TypographyStyles } from 'src/libraries/wpl/foundations/src/typography-styles';

import { getFirstName } from '@/components/signup/utils/get-first-name';
import { expectContent } from '@/content/utils/expectContent';
import { FourOhFourPageTemplate } from '@/templates/404/404-page/src/404-page.template';
import { isProduction } from '@/utils/urls';

import { enhanceComposition } from '../clients/uniform-client/enhancers';
import { useUserContext } from '../components/user-context/user-context-provider';
import { parseRoute } from '../wac-routes';

import { getCompositionCdn } from './get-composition-cdn';
import UNIFORM_DEFINITIONS from './uniform-definitions';
import { getLiveComposition } from './utils/get-live-composition';
import { UniformMissingComponent } from './utils/uniform-missing-component';
import { useWAEContext } from './utils/wae-context-provider';

declare global {
	interface Window {
		_uniformPreloadedComposition?: RootComponentInstance;
	}
}

export type UniformWrapperProps = {
	composition?: RootComponentInstance;
	isUniformPlayground?: boolean;
	isAllowPostMessage?: boolean;
};

const resolveRenderer: RenderComponentResolver = ({ type }) => {
	const MissingComponent = () => <UniformMissingComponent componentName={type} />;
	return UNIFORM_DEFINITIONS[type] ?? MissingComponent;
};

export const UniformWrapper = ({
	composition,
	isUniformPlayground,
	isAllowPostMessage,
}: UniformWrapperProps) => {
	const preloadedComposition =
		typeof window !== 'undefined' ? window._uniformPreloadedComposition : null;

	const initialData = preloadedComposition || composition;

	const [baseComposition, setBaseComposition] = useState(initialData);
	const [isFetched, setIsFetched] = useState(!!initialData);
	const [shouldRender404, setShouldRender404] = useState(false);

	// NOTE: the legacy postMessage flow (UNIFORM_COMPOSITION_UPDATE) used
	// to live here as a separate useEffect. It was removed because the
	// full composition JSON was too large to chunk over postMessage
	// reliably. The ad-hoc preview flow now uses compositionId+projectId
	// URL params instead (see fetchFromCanvasApi below).

	useEffect(() => {
		const updateDocumentMetaFromComposition = (comp: RootComponentInstance | undefined) => {
			if (typeof document === 'undefined' || !comp?.parameters) return;

			const title = comp.parameters['pageTitle']?.value as string | undefined;
			const description = comp.parameters['metaDescription']?.value as string | undefined;

			if (title) {
				document.title = title;
			}
			if (description) {
				let metaDesc = document.querySelector('meta[name="description"]');
				if (!metaDesc) {
					metaDesc = document.createElement('meta');
					metaDesc.setAttribute('name', 'description');
					document.head.appendChild(metaDesc);
				}
				metaDesc.setAttribute('content', description);
			}
		};

		// When compositionSource is live, fetch the live composition from WACMAN
		const loadLiveComposition = async (queryParams: URLSearchParams) => {
			const { path, locale } = parseRoute(window.location);
			const liveComposition = await getLiveComposition(queryParams, path, locale);
			const enhancedComp = await enhanceComposition(liveComposition);
			setBaseComposition(enhancedComp);
			updateDocumentMetaFromComposition(enhancedComp);
			setShouldRender404(false);
			setIsFetched(true);
		};

		const queryParams = new URLSearchParams(window.location.search);
		const compositionSource = queryParams.get('compositionSource');
		if (compositionSource === 'live' && !isProduction() && !baseComposition) {
			loadLiveComposition(queryParams);
			return;
		}

		// When isAllowPostMessage is true and compositionId is in the URL,
		// we need to fetch from the Canvas API even if initialData exists
		// (because initialData may be EMPTY_COMPOSITION from SSR).
		const hasCompositionIdInUrl =
			typeof window !== 'undefined' &&
			new URLSearchParams(window.location.search).has('compositionId');

		if (initialData && !(isAllowPostMessage && hasCompositionIdInUrl)) {
			return;
		}

		const addNoIndexTag = () => {
			if (typeof document !== 'undefined') {
				let robotsMeta = document.querySelector('meta[name="robots"]');
				if (!robotsMeta) {
					robotsMeta = document.createElement('meta');
					robotsMeta.setAttribute('name', 'robots');
					document.head.appendChild(robotsMeta);
				}
				robotsMeta.setAttribute('content', 'noindex,nofollow');
			}
		};

		const fetchFromCanvasApi = async (
			compositionId: string,
			projectId: string,
			previewLocale: string,
		) => {
			const isLocalhost =
				window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

			// On localhost, proxy through the local dev server (avoids CORS + keeps
			// the API key out of the browser even in local dev — the dev server
			// reads UNIFORM_API_KEY from .env.local / .env.dev).
			// On staging/prod, proxy through wac-management-service which holds the
			// Uniform API key server-side — never call uniform.app directly from the
			// browser as that triggers CORS errors AND would leak the API key.
			const buildCanvasUrl = (state: string) => {
				if (isLocalhost) {
					return `/api/uniform-canvas-proxy?compositionId=${encodeURIComponent(
						compositionId,
					)}&state=${state}&projectId=${encodeURIComponent(
						projectId,
					)}&locale=${encodeURIComponent(previewLocale)}`;
				}
				// process.env.X is inlined at client-bundle build time by
				// Atlaspack — the var must be listed in `inlineEnvironment`
				// under `@atlaspack/transformer-js` in package.json (same
				// mechanism as WAC_MANAGEMENT_CDN above).
				const wacmanBase = process.env['WAC_MANAGEMENT_API_BASE_URL'] ?? '';
				return `${wacmanBase}/api/v1/uniform/composition/get?compositionId=${encodeURIComponent(
					compositionId,
				)}&projectId=${encodeURIComponent(projectId)}&state=${encodeURIComponent(
					state,
				)}&locale=${encodeURIComponent(previewLocale)}`;
			};

			// Default to draft state (0) since the AI migration tooling
			// creates ad-hoc compositions in draft. Callers can override
			// by passing &state=64 (published) in the URL if they really
			// want the published version of a published composition.
			// No credentials needed — WACMAN supplies the Uniform API key
			// server-side. On localhost the dev-server proxy also handles auth.
			const params = new URLSearchParams(window.location.search);
			const requestedState = params.get('state') || '0';
			const res = await fetch(buildCanvasUrl(requestedState), {});
			if (!res.ok) {
				console.error(`Canvas API error: ${res.status}`);
				setShouldRender404(true);
				setIsFetched(true);
				return;
			}
			const data = await res.json();
			const enhancedComp = await enhanceComposition(data.composition);
			setBaseComposition(enhancedComp);
			setShouldRender404(false);
			setIsFetched(true);
			console.log(`uniform-preview: composition loaded from Canvas API (state=${requestedState})`);
		};

		// oxlint-disable-next-line complexity
		// eslint-disable-next-line complexity
		const fetchComposition = async () => {
			if (typeof window === 'undefined') return;

			const { path, locale } = parseRoute(window.location);

			if (path === 'uniform-preview' && isAllowPostMessage === true) {
				// Check for compositionId query param — if present, fetch directly
				// from the Uniform Canvas API. This is the preferred (and now
				// only) approach for ad-hoc migration validation since the
				// legacy postMessage flow was removed.
				const params = new URLSearchParams(window.location.search);
				const compositionId = params.get('compositionId');
				const projectId = params.get('projectId');

				if (compositionId && projectId) {
					// Get locale from query param or default to en-US.
					// Required to resolve localized parameters correctly.
					const previewLocale = params.get('locale') || 'en-US';

					console.log('uniform-preview: fetching composition directly from Canvas API', {
						compositionId,
						projectId,
						locale: previewLocale,
					});
					try {
						await fetchFromCanvasApi(compositionId, projectId, previewLocale);
					} catch (error) {
						console.error('Error fetching composition from Canvas API:', error);
						setShouldRender404(true);
						setIsFetched(true);
					}
					return;
				}

				console.log(
					'uniform-preview with allowPostMessage - compositionId / projectId missing, nothing to render',
				);
				return;
			}

			const slug = path.startsWith('/') ? path.substring(1) : path;

			const cdn = getCompositionCdn();
			const cdnPath = `release/${locale}/${slug}/composition.json`;

			const cdnComposition = `${cdn}${cdnPath.replace(/\/\//g, '/')}`;

			try {
				const req = await fetch(cdnComposition);
				setShouldRender404(req.status !== 200);
				if (req.status === 404) {
					setIsFetched(true);
					return;
				}
				const file = await req.json();
				const enhancedComp = await enhanceComposition(file.compositionApiResponse.composition);
				setBaseComposition(enhancedComp);
				setShouldRender404(false);
				setIsFetched(true);

				if (enhancedComp?.parameters?.['noIndex']?.value) {
					addNoIndexTag();
				}
			} catch (error) {
				console.error('Error fetching composition from CDN:', error);
				setShouldRender404(true);
				setIsFetched(true);
			}
		};

		fetchComposition();
	}, [initialData, composition, isAllowPostMessage]);

	const { user } = useUserContext();
	const { context } = useUniformContext();
	const { personalizationValues } = useWAEContext();

	useMemo(() => {
		context.update({
			quirks: {
				...personalizationValues,
			},
		});
	}, [context, personalizationValues]);

	const personalizedComposition = useMemo(() => {
		const tokens = new Map([
			['{{name}}', user?.name || ''],
			['{{first-name}}', getFirstName(user?.name || '')],
		]);
		let jsonString = JSON.stringify(baseComposition);
		if (!jsonString) return baseComposition;

		jsonString = jsonString.replace(/\{\{[a-zA-Z_-]*\}\}/g, (match) => {
			return tokens.get(match) || match;
		});
		return JSON.parse(jsonString) as RootComponentInstance;
	}, [baseComposition, user?.name]);

	useEffect(() => {
		const updateQuirks = async () => {
			if (!user?.name) {
				return;
			}
			const quirks: Quirks = {
				...context.quirks,
				name: user.name,
				'first-name': getFirstName(user.name),
			};
			await context.update({ quirks });
		};

		updateQuirks();
	}, [user?.name, context]);

	if (isUniformPlayground) {
		const typographyStylesDecorator: UniformPlaygroundDecorator = ({ children }) => (
			<TypographyStyles>{children}</TypographyStyles>
		);

		return (
			<UniformPlayground
				behaviorTracking="onLoad"
				resolveRenderer={resolveRenderer}
				contextualEditingEnhancer={({ composition }) => enhanceComposition(composition)}
				decorators={[typographyStylesDecorator]}
			/>
		);
	}

	return shouldRender404 ? (
		<FourOhFourPageTemplate />
	) : (
		isFetched && (
			<div>
				<UniformComposition
					data={expectContent(
						(personalizedComposition || baseComposition) as RootComponentInstance,
					)}
					behaviorTracking="onLoad"
					resolveRenderer={resolveRenderer}
					contextualEditingEnhancer={({ composition }) => enhanceComposition(composition)}
				/>
			</div>
		)
	);
};
